import { UploadProgressEvent } from '../components/UploadProgressDialog';

export interface SSEHandlerOptions {
  onEvent: (event: UploadProgressEvent) => void;
  onError?: (error: Error) => void;
  onComplete?: () => void;
}

export const handleSSEUpload = async (
  url: string,
  formData: FormData,
  options: SSEHandlerOptions
): Promise<void> => {
  const { onEvent, onError, onComplete } = options;

  try {
    console.log('[SSE] Starting upload to:', url);
    
    const token = localStorage.getItem('token');
    console.log('[SSE] Token present:', !!token);

    const response = await fetch(url, {
      method: 'POST',
      body: formData,
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    console.log('[SSE] Response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[SSE] Error response:', errorText);
      throw new Error(`Upload failed: ${response.statusText}. ${errorText}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body available');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      
      if (done) {
        console.log('[SSE] Stream complete');
        if (onComplete) onComplete();
        break;
      }

      // Decode chunk and add to buffer
      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;

      // Process complete messages (ending with \n\n)
      const messages = buffer.split('\n\n');
      buffer = messages.pop() || ''; // Keep incomplete message in buffer

      for (const message of messages) {
        if (!message.trim()) continue;

        const lines = message.split('\n');
        let eventType = '';
        let eventData = '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            eventType = line.substring(6).trim();
          } else if (line.startsWith('data:')) {
            eventData = line.substring(5).trim();
          }
        }

        if (eventType && eventData) {
          try {
            const data = JSON.parse(eventData);
            console.log('[SSE] Event received:', eventType, data);
            onEvent({ type: eventType as any, data });
          } catch (e) {
            console.error('[SSE] Failed to parse event data:', e, eventData);
          }
        }
      }
    }
  } catch (error: any) {
    console.error('[SSE] Error:', error);
    if (onError) {
      onError(error);
    }
    throw error;
  }
};

export const handleSSEImport = async (
  url: string,
  data: any[],
  options: SSEHandlerOptions
): Promise<void> => {
  const { onEvent, onError, onComplete } = options;

  try {
    console.log('[SSE] Starting import to:', url);
    
    const token = localStorage.getItem('token');
    console.log('[SSE] Token present:', !!token);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ data }),
    });

    console.log('[SSE] Response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[SSE] Error response:', errorText);
      throw new Error(`Import failed: ${response.statusText}. ${errorText}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body available');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      
      if (done) {
        console.log('[SSE] Stream complete');
        if (onComplete) onComplete();
        break;
      }

      // Decode chunk and add to buffer
      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;

      // Process complete messages (ending with \n\n)
      const messages = buffer.split('\n\n');
      buffer = messages.pop() || ''; // Keep incomplete message in buffer

      for (const message of messages) {
        if (!message.trim()) continue;

        const lines = message.split('\n');
        let eventType = '';
        let eventData = '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            eventType = line.substring(6).trim();
          } else if (line.startsWith('data:')) {
            eventData = line.substring(5).trim();
          }
        }

        if (eventType && eventData) {
          try {
            const data = JSON.parse(eventData);
            console.log('[SSE] Event received:', eventType, data);
            onEvent({ type: eventType as any, data });
          } catch (e) {
            console.error('[SSE] Failed to parse event data:', e, eventData);
          }
        }
      }
    }
  } catch (error: any) {
    console.error('[SSE] Error:', error);
    if (onError) {
      onError(error);
    }
    throw error;
  }
};
